import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronDown,
  PlayCircle,
  ArrowRight,
  Heart,
  HandHeart,
  Users,
  BookOpen,
  Sparkles,
  Quote,
  MapPin,
} from "lucide-react";
import { useEffect, useState } from "react";
import { church, fullAddress } from "@/config/church";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useReveal from "@/hooks/useReveal";
import FamilyDayCountdown from "@/components/FamilyDayCountdown";
import { SectionHeader } from "@/components/Section";

const HERO_IMAGE =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/bf7g84zx_IMG_5577.jpg";

const ministryHighlights = [
  {
    title: "Men",
    verse: "1 Corinthians 16:13",
    image:
      "https://images.pexels.com/photos/13172188/pexels-photo-13172188.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    title: "Marriage",
    verse: "Ephesians 5:25",
    image:
      "https://images.pexels.com/photos/9577192/pexels-photo-9577192.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    title: "Bible Study",
    verse: "2 Timothy 2:15",
    image:
      "https://images.pexels.com/photos/6284493/pexels-photo-6284493.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    title: "Prayer Ministry",
    verse: "James 5:16",
    image:
      "https://images.pexels.com/photos/5876583/pexels-photo-5876583.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

const testimonials = [
  {
    name: "Michelle B.",
    quote:
      "The moment I walked through the doors of New Philadelphia, I felt like I was home. God met me here.",
  },
  {
    name: "James & Alicia",
    quote:
      "Pastor Little's preaching challenged us as a couple to build our marriage on the Rock. Our whole home is different.",
  },
  {
    name: "Deacon Terrance",
    quote:
      "This is a church where tradition is honored and transformation is expected. It has grown my faith in ways I never imagined.",
  },
];

export default function Home() {
  useReveal();
  const [sermon, setSermon] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/sermons");
        setSermon(s.data?.[0] || null);
      } catch {
        /* silent */
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* FRIENDS & FAMILY DAY PROMO BAND */}
      <Link
        to="/family-day"
        data-testid="home-family-day-promo"
        className="fixed top-0 inset-x-0 z-40 block group"
      >
        <div className="bg-gradient-to-r from-npbc-red via-[#a80d26] to-npbc-red text-white">
          <div className="container-npbc py-2.5 flex items-center justify-center gap-3 text-center">
            <span className="hidden sm:inline-flex h-2 w-2 rounded-full bg-npbc-gold animate-pulse" />
            <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] sm:tracking-[0.22em] text-npbc-gold">
              Oct 11
            </span>
            <span className="text-xs sm:text-sm font-semibold">
              Friends &amp; Family Day &mdash; RSVP Now
            </span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </Link>

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative isolate overflow-hidden min-h-[100svh] flex items-center text-white"
      >
        <img
          src={HERO_IMAGE}
          alt="Congregation lifting hands in worship"
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-npbc-black/70 via-npbc-black/55 to-npbc-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-npbc-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 grain" />

        <div className="container-npbc relative z-10 pt-28 pb-24">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center gap-3"
            >
              <div className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur">
                <img src={church.logo} alt="" className="h-6 w-6 object-contain" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/90">
                  New Philadelphia Missionary Baptist Church · NPMBC
                </span>
              </div>
              <Link
                to="/family-day"
                data-testid="hero-family-day-badge"
                className="group relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-npbc-red via-[#e11d3d] to-npbc-red px-4 py-2 shadow-[0_10px_30px_-5px_rgba(200,16,46,0.6)] hover:shadow-[0_14px_40px_-5px_rgba(200,16,46,0.9)] hover:scale-[1.02] transition-all"
              >
                <span className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-npbc-gold via-white/70 to-npbc-gold opacity-50 blur-md group-hover:opacity-90 transition-opacity" />
                <span className="relative inline-flex items-center gap-2">
                  <span className="text-[13px]" role="img" aria-label="party">🎉</span>
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.22em] text-npbc-gold">
                    Oct 11
                  </span>
                  <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                    Family &amp; Friends Day
                  </span>
                  <span className="sm:hidden text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                    Family Day
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-white transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 heading-serif text-6xl sm:text-7xl md:text-[92px] leading-[0.92] text-balance"
            >
              {church.welcomeHeadline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-xl text-lg md:text-xl text-white/85 leading-relaxed"
            >
              {church.welcomeSubheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <Link to="/visit" data-testid="hero-plan-visit" className="btn-primary">
                Plan Your Visit
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={church.social.facebookLive}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="hero-watch-live"
                className="btn-outline"
              >
                <PlayCircle className="h-4 w-4" />
                Watch Live
              </a>
            </motion.div>

            {/* Service times pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl"
            >
              {church.services.map((s) => (
                <div
                  key={s.name}
                  data-testid={`service-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="glass-dark rounded-2xl px-4 py-3"
                >
                  <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-npbc-gold">
                    {s.day}
                  </div>
                  <div className="mt-1 heading-serif text-xl text-white">{s.name}</div>
                  <div className="text-sm text-white/75">{s.time}</div>
                  {s.zoom && (
                    <a
                      href={s.zoom}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="hero-bible-study-zoom-btn"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-npbc-gold px-3 py-1 text-[11px] font-bold text-npbc-black hover:brightness-95 transition"
                    >
                      Join on Zoom →
                    </a>
                  )}
                  {!s.zoom && s.note && (
                    <div className="mt-1 text-[11px] text-white/60">{s.note}</div>
                  )}
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70">
          <span className="text-[10px] uppercase tracking-[0.28em]">Scroll</span>
          <ChevronDown className="h-5 w-5 animate-scroll-hint" />
        </div>
      </section>

      {/* FRIENDS & FAMILY DAY — LIVE COUNTDOWN */}
      <FamilyDayCountdown />

      {/* PASTOR WELCOME */}
      <section className="relative bg-white py-24 md:py-32">
        <div className="container-npbc grid gap-12 lg:grid-cols-[1.6fr_1fr] items-center">
          <div data-reveal className="order-2 lg:order-1">
            <div className="eyebrow">A Word From Our Pastor</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.02]">
              You were not brought here <span className="italic text-npbc-red">by accident.</span>
            </h2>
            <p className="mt-6 text-npbc-gray text-[17px] leading-relaxed">
              {church.pastor.bio}
            </p>
            <blockquote className="mt-8 border-l-2 border-npbc-gold pl-5 italic text-npbc-black/80">
              "At New Philadelphia we honor the tradition of our fathers and mothers in the faith,
              while pressing forward in the transforming power of Jesus Christ. Come as you
              are — but don't leave the same way."
            </blockquote>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/leadership" className="btn-ghost-dark">
                Meet Our Leadership
              </Link>
              <Link to="/about" className="text-npbc-red font-semibold inline-flex items-center gap-2 group px-4 py-3">
                Our Story
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          <div data-reveal className="order-1 lg:order-2 mx-auto lg:mx-0 w-full max-w-[380px]">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-npbc-red/20 via-npbc-black/10 to-npbc-gold/20 rounded-2xl -rotate-1" />
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-white shadow-[0_20px_50px_-25px_rgba(0,0,0,0.4)]">
                <img
                  src={church.pastor.image}
                  alt={church.pastor.name}
                  className="h-full w-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 text-center">
                <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-npbc-red">
                  Senior Pastor
                </div>
                <div className="heading-serif text-xl mt-1 text-npbc-black">
                  {church.pastor.name}
                </div>
              </div>
              {/* Actual sanctuary photo — a glimpse of the family */}
              <div className="mt-6 rounded-2xl overflow-hidden border border-black/[0.06] shadow-lg">
                <img
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/gz83939m_709226385_122123167863216091_9060692169340108500_n.jpg"
                  alt="Inside New Philadelphia Missionary Baptist Church"
                  className="w-full h-40 object-cover"
                  loading="lazy"
                />
                <div className="p-3 bg-white text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-gray/70">
                    A glimpse of our church family
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="relative bg-npbc-cream py-24 md:py-32">
        <div className="container-npbc">
          <div className="grid gap-14 lg:grid-cols-2">
            <div data-reveal className="card-elevated p-10 md:p-12 relative overflow-hidden">
              <div className="absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-npbc-red/8 blur-2xl" />
              <div className="eyebrow">Our Mission</div>
              <h3 className="mt-3 heading-serif text-4xl leading-tight">
                To lead every soul into a deeper relationship with Christ.
              </h3>
              <p className="mt-5 text-npbc-gray leading-relaxed">
                We exist to preach the gospel, disciple believers, love our neighbors,
                and send transformed people into the world as ambassadors of the Kingdom.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="h-11 w-11 rounded-full bg-npbc-red text-white grid place-items-center">
                  <HandHeart className="h-5 w-5" />
                </div>
                <div className="text-sm font-semibold tracking-wide text-npbc-black">
                  Preach · Disciple · Send
                </div>
              </div>
            </div>

            <div data-reveal className="card-elevated p-10 md:p-12 relative overflow-hidden bg-npbc-black text-white">
              <div className="absolute left-0 bottom-0 h-40 w-40 translate-y-1/2 -translate-x-1/3 rounded-full bg-npbc-gold/15 blur-2xl" />
              <div className="eyebrow !text-npbc-gold">Our Vision</div>
              <h3 className="mt-3 heading-serif text-4xl leading-tight">
                A church where <span className="text-npbc-gold">tradition meets transformation.</span>
              </h3>
              <p className="mt-5 text-white/75 leading-relaxed">
                To be a beacon in Memphis and beyond — anchored in the historic faith,
                open to the fresh move of the Spirit, and known for the love of Christ
                across every generation.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="h-11 w-11 rounded-full bg-npbc-gold text-npbc-black grid place-items-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="text-sm font-semibold tracking-wide">
                  Anchored · Alive · Sent
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST SERMON */}
      {sermon && (
        <section className="relative bg-npbc-black text-white py-24 md:py-32 overflow-hidden">
          <img
            src={sermon.thumbnail}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-npbc-black via-npbc-black/85 to-npbc-black/50" />
          <div className="container-npbc relative grid gap-14 lg:grid-cols-[1.1fr_1fr] items-center">
            <div data-reveal>
              <div className="eyebrow !text-npbc-gold">Latest Sermon</div>
              <h2 className="mt-3 heading-serif text-5xl md:text-6xl leading-[1.02]">
                {sermon.title}
              </h2>
              <p className="mt-4 text-white/70 text-lg leading-relaxed">
                {sermon.description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <span className="text-npbc-gold font-semibold">{sermon.speaker}</span>
                <span>· {sermon.scripture}</span>
                <span>· {sermon.duration}</span>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/media" className="btn-primary">
                  <PlayCircle className="h-4 w-4" /> Watch Sermon
                </Link>
                <Link to="/media" className="btn-outline">
                  All Sermons
                </Link>
              </div>
            </div>

            <div data-reveal className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-npbc-red/25 to-npbc-gold/25 blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] max-w-[420px] mx-auto bg-white shadow-2xl border border-white/10">
                <img
                  src={church.pastor.image}
                  alt={`${church.pastor.name} preaching`}
                  className="h-full w-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-npbc-black via-npbc-black/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-npbc-gold">
                    Preaching Today
                  </div>
                  <div className="mt-1 heading-serif text-xl leading-tight">
                    {church.pastor.name}
                  </div>
                  <a
                    href={church.social.facebookLive}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="home-latest-sermon-play"
                    className="mt-4 inline-flex items-center gap-3 rounded-full bg-white/95 backdrop-blur pl-3 pr-5 py-2 text-npbc-black font-semibold text-sm hover:scale-[1.02] transition"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-npbc-red text-white">
                      <PlayCircle className="h-4 w-4" />
                    </span>
                    Play sermon
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MINISTRY HIGHLIGHTS */}
      <section className="relative bg-white py-24 md:py-32">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              eyebrow="Ministry Highlights"
              title="There is a place for you here."
              subtitle="From little ones taking their first steps of faith to grandparents mentoring the next generation — every season of life has a home."
            />
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ministryHighlights.map((m, i) => (
              <Link
                to="/ministries"
                key={m.title}
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`ministry-card-${m.title.toLowerCase()}`}
                className="group relative block h-[380px] overflow-hidden rounded-2xl"
              >
                <img
                  src={m.image}
                  alt={m.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-gold">
                    {m.verse}
                  </div>
                  <div className="mt-2 heading-serif text-3xl leading-tight">{m.title}</div>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/ministries" className="btn-ghost-dark">
              Explore All Ministries <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative bg-npbc-cream py-24 md:py-32">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              align="center"
              eyebrow="Lives Changed"
              title="Stories from our family."
            />
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                data-reveal
                style={{ transitionDelay: `${i * 80}ms` }}
                className="card-elevated p-8 relative"
              >
                <Quote className="h-8 w-8 text-npbc-red/25 absolute top-6 right-6" />
                <p className="heading-serif text-xl leading-snug text-npbc-black italic">
                  “{t.quote}”
                </p>
                <figcaption className="mt-6 text-sm font-semibold tracking-wide text-npbc-red">
                  — {t.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PRAYER + GIVE CTAs */}
      <section className="relative bg-white py-24 md:py-32">
        <div className="container-npbc grid gap-6 lg:grid-cols-2">
          <PrayerCTA />

          <div
            data-reveal
            className="relative rounded-3xl overflow-hidden p-10 md:p-14 min-h-[420px] flex flex-col justify-end text-white"
          >
            <img
              src="https://images.pexels.com/photos/5876583/pexels-photo-5876583.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Giving hands"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-npbc-black/95 via-npbc-black/60 to-transparent" />
            <div className="relative">
              <div className="eyebrow !text-npbc-gold">Give With Joy</div>
              <h3 className="mt-3 heading-serif text-4xl md:text-5xl leading-[1.05]">
                Your generosity fuels the mission.
              </h3>
              <p className="mt-4 max-w-lg text-white/80">
                Every gift helps us preach the gospel, disciple believers, and serve our
                city. Give securely online or through Cash App.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/give" data-testid="home-give-btn" className="btn-gold">
                  <Heart className="h-4 w-4" /> Give Now
                </Link>
                <a
                  href={church.giving.cashappUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-outline"
                >
                  Cash App {church.giving.cashapp}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR HOME — the actual church building */}
      <section className="relative bg-white py-24">
        <div className="container-npbc grid gap-14 lg:grid-cols-[1.05fr_1fr] items-center">
          <div data-reveal className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-npbc-red/10 to-npbc-gold/20 -rotate-1" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/vxoldu53_616955902_122105707497216091_3578243584108528149_n.jpg"
              alt="New Philadelphia Missionary Baptist Church, 533 S. Mendenhall Rd, Memphis TN"
              className="relative rounded-3xl w-full h-[480px] object-cover shadow-2xl"
              loading="lazy"
            />
          </div>
          <div data-reveal>
            <div className="eyebrow">Our Home on Mendenhall</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              Look for the red-brick sanctuary with the arched stained-glass windows.
            </h2>
            <p className="mt-5 text-npbc-gray leading-relaxed text-[17px]">
              Our historic sanctuary has stood on 533 S. Mendenhall Road for generations —
              a red-brick house of worship with beautiful arched stained-glass windows,
              built by the faith of families who came before us and kept open by the faithfulness
              of God.
            </p>
            <p className="mt-4 text-npbc-gray leading-relaxed">
              Come see it in person this Sunday. We'd love to meet you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/visit" className="btn-primary">
                Plan Your Visit <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-ghost-dark"
              >
                <MapPin className="h-4 w-4" /> Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VISIT BAND */}
      <section className="relative bg-npbc-black text-white py-20">
        <div className="container-npbc grid gap-8 lg:grid-cols-[1.4fr_1fr] items-center">
          <div data-reveal>
            <div className="eyebrow !text-npbc-gold">Come As You Are</div>
            <h2 className="mt-3 heading-serif text-4xl md:text-5xl">
              We'd love to meet you this Sunday.
            </h2>
            <p className="mt-4 max-w-2xl text-white/75">
              First-time guest? Let us know you're coming and we'll roll out the red carpet.
            </p>
          </div>
          <div data-reveal className="flex flex-col gap-4 lg:items-end">
            <div className="flex items-center gap-3 text-white/85">
              <MapPin className="h-5 w-5 text-npbc-red" /> {fullAddress}
            </div>
            <Link to="/visit" className="btn-primary self-start lg:self-end">
              Plan Your Visit <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Inline prayer CTA card with quick submit */
function PrayerCTA() {
  const navigate = useNavigate();
  const [request, setRequest] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !request) return;
    try {
      setBusy(true);
      await api.post("/prayer", { name, request });
      setName("");
      setRequest("");
      navigate("/thank-you?source=prayer");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-reveal className="card-elevated p-8 md:p-10 relative overflow-hidden">
      <div className="absolute right-0 top-0 h-40 w-40 -translate-y-1/3 translate-x-1/3 rounded-full bg-npbc-red/8 blur-2xl" />
      <div className="eyebrow">Prayer Request</div>
      <h3 className="mt-3 heading-serif text-3xl md:text-4xl leading-[1.1]">
        Let us pray with you.
      </h3>
      <p className="mt-3 text-npbc-gray">
        Whatever you're carrying — bring it to Jesus. Our prayer team receives every
        request confidentially and prays over each one.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="home-prayer-name"
          placeholder="Your name"
          className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20 transition"
          required
        />
        <textarea
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          data-testid="home-prayer-request"
          placeholder="What can we pray with you about?"
          rows={4}
          className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20 transition"
          required
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="home-prayer-submit"
          className="btn-primary w-full justify-center"
        >
          {busy ? "Sending..." : "Send Prayer Request"}
          <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
